import { Injectable, Component, ChangeDetectionStrategy, NgZone, Input, HostBinding, ViewChild, NgModule } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, fromEvent, combineLatest, of, asyncScheduler, Observable } from 'rxjs';
import { map, tap, filter, switchMap, observeOn } from 'rxjs/operators';
import { v4 } from 'uuid';
import { CommonModule } from '@angular/common';

import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/common';

const _c0 = ["main"];
const _c1 = ["anim"];
const _c2 = ["eraserCursor"];
const _c3 = function (a0) { return { "is-full-size": a0 }; };
var CommandName;
(function (CommandName) {
    CommandName["Clear"] = "clear";
    CommandName["Load"] = "load";
    CommandName["Undo"] = "undo";
    CommandName["Redo"] = "redo";
})(CommandName || (CommandName = {}));
/**
 * Command clears entire main canvas content.
 */
class ClearCommand {
    constructor() {
        this.name = CommandName.Clear;
    }
}
/**
 * Command allows to load new state of main canvas from dataUrl.
 */
class LoadCommand {
    /**
     * Command allows to load new state of main canvas from dataUrl.
     * @param state DataUrl of image. Example: `data:image/png;base64,iVBORw0KGgoAA...`
     */
    constructor(state) {
        this.state = state;
        this.name = CommandName.Load;
    }
}
const CANVAS_DEFAULT_WIDTH_PX = 1800;
const CANVAS_DEFAULT_HEIGHT_PX = 900;
/**
 * Command moves main canvas in time one step backward.
 */
class UndoCommand {
    constructor() {
        this.name = CommandName.Undo;
    }
}
/**
 * Command moves main canvas in time one step forward. Works only if canvas has been undone before.
 */
class RedoCommand {
    constructor() {
        this.name = CommandName.Redo;
    }
}

class TextbookDrawerService {
    constructor() {
        this.color$ubject = new BehaviorSubject('#000000');
        this.size$ubject = new BehaviorSubject(10);
        this.toolName$ubject = new BehaviorSubject(undefined);
        this.command$subject = new Subject();
        this.undoLength$ubject = new BehaviorSubject(0);
        this.redoLength$ubject = new BehaviorSubject(0);
        this.currentState$ubject = new BehaviorSubject('');
        this.isLoading$ubject = new BehaviorSubject(false);
        this.isLocked$ubject = new BehaviorSubject(false);
    }
    get color$() {
        return this.color$ubject.asObservable();
    }
    get size$() {
        return this.size$ubject.asObservable();
    }
    get toolName$() {
        return this.toolName$ubject.asObservable();
    }
    get command$() {
        return this.command$subject.asObservable();
    }
    /**
     * Amount of stored undo states. Can be used to block undo button.
     */
    get undoLength$() {
        return this.undoLength$ubject.asObservable();
    }
    /**
     * Amount of stored redo states. Can be used to block redo button.
     */
    get redoLength$() {
        return this.redoLength$ubject.asObservable();
    }
    get currentState$() {
        return this.currentState$ubject.asObservable();
    }
    /**
     * currently displayed dataUrl of canvas image
     */
    get currentState() {
        return this.currentState$ubject.getValue() || undefined;
    }
    /**
     * returns true if canvas is empty from any drawings
     */
    get isCanvasClear$() {
        return this.currentState$.pipe(map((state) => {
            if (!state) {
                return true;
            }
            if (!this.ctx) {
                throw new Error('Canvas not set');
            }
            return this.haveCanvasNonZeroValues(this.ctx);
        }));
    }
    /**
     * returns true if canvas is empty from any drawings
     */
    get isCanvasClear() {
        if (!this.currentState) {
            return true;
        }
        if (!this.ctx) {
            throw new Error('Canvas not set');
        }
        return this.haveCanvasNonZeroValues(this.ctx);
    }
    /**
     * Truthy when new canvas image is loading
     */
    get isLoading$() {
        return this.isLoading$ubject.asObservable();
    }
    get isLocked$() {
        return this.isLocked$ubject.asObservable();
    }
    setColor(color) {
        this.color$ubject.next(color);
    }
    getColor() {
        return this.color$ubject.getValue();
    }
    setSize(size) {
        this.size$ubject.next(size);
    }
    getSize() {
        return this.size$ubject.getValue();
    }
    setToolName(tool) {
        this.toolName$ubject.next(tool);
    }
    getToolName() {
        return this.toolName$ubject.getValue();
    }
    /**
     * Clears entire main canvas content.
     */
    clear() {
        this.command$subject.next(new ClearCommand());
    }
    /**
     * Load new state of main canvas from dataUrl.
     * @param state DataUrl of image. Example: `data:image/png;base64,iVBORw0KGgoAA...`
     */
    load(state) {
        this.command$subject.next(new LoadCommand(state));
    }
    /**
     * Move main canvas in time one step backward.
     */
    undo() {
        this.command$subject.next(new UndoCommand());
    }
    /**
     * Move main canvas in time one step forward. Works only if canvas has been undone before.
     */
    redo() {
        this.command$subject.next(new RedoCommand());
    }
    /** Internal method. Usage not recommended */
    _setUndoLength(length) {
        this.undoLength$ubject.next(length);
    }
    setLocked(isLocked) {
        return this.isLocked$ubject.next(isLocked);
    }
    // PACKAGE INTERNALS
    /** Internal method. Usage not recommended */
    _setRedoLength(length) {
        this.redoLength$ubject.next(length);
    }
    /** Internal method. Usage not recommended */
    _setCurrentState(state) {
        this.currentState$ubject.next(state);
    }
    /** Internal method. Usage not recommended */
    _setIsLoading(isLoading) {
        this.isLoading$ubject.next(isLoading);
    }
    /** Internal method. Usage not recommended */
    _setMainCanvas(canvas) {
        this.ctx = (canvas === null || canvas === void 0 ? void 0 : canvas.getContext('2d')) || undefined;
    }
    ngOnDestroy() {
        this.color$ubject.complete();
        this.size$ubject.complete();
        this.toolName$ubject.complete();
        this.command$subject.complete();
        this.undoLength$ubject.complete();
        this.redoLength$ubject.complete();
        this.currentState$ubject.complete();
        this.isLoading$ubject.complete();
        this.isLocked$ubject.complete();
    }
    haveCanvasNonZeroValues(ctx) {
        const { width, height } = ctx.canvas;
        const imageBuffer = ctx.getImageData(0, 0, width, height).data.buffer;
        return !new Uint32Array(imageBuffer).some((x) => x !== 0);
    }
}
TextbookDrawerService.ɵfac = function TextbookDrawerService_Factory(t) { return new (t || TextbookDrawerService)(); };
TextbookDrawerService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: TextbookDrawerService, factory: TextbookDrawerService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TextbookDrawerService, [{
        type: Injectable
    }], function () { return []; }, null); })();

class Tool {
    constructor(
    /**
     * Context of canvas which presents the current state of drawing (image).
     */
    mainCtx, 
    /**
     * Context of canvas used to animations that shouldn't update main image.
     */
    animCtx, 
    /**
     * Should be called after each completed step of drawing.
     */
    updateState) {
        this.mainCtx = mainCtx;
        this.animCtx = animCtx;
        this.updateState = updateState;
        /**
         * Used to block handlers.
         */
        this.isLocked = false;
        this.mainCtx.lineJoin = this.animCtx.lineJoin = 'round';
        this.mainCtx.lineCap = this.animCtx.lineCap = 'round';
        this.mainCtx.globalCompositeOperation = this.animCtx.globalCompositeOperation = 'source-over';
    }
    /**
     * Allow to lock entire tool, to not respond for events.
     */
    setLocked(isLocked) {
        this.isLocked = isLocked;
    }
    /**
     * Allow to set color of tool. Tools can interpret this in a different way.
     */
    setSize(size) {
        this.size = size;
    }
    /**
     * Allow to set size of tool. Tools can interpret this in a different way.
     */
    setColor(color) {
        this.color = color;
    }
    /**
     * Logic of new tool goes here.
     */
    onMouseDown(e) { }
    /**
     * Logic of new tool goes here.
     */
    onMouseMove(e) { }
    /**
     * Logic of new tool goes here.
     */
    onMouseUp(e) { }
    /**
     * Logic of new tool goes here. Default it fallbacks to `this.onMouseDown`.
     */
    onTouchStart(e) {
        this.onMouseDown(e);
    }
    /**
     * Logic of new tool goes here. Default it fallbacks to `this.onMouseMove`.
     */
    onTouchMove(e) {
        this.onMouseMove(e);
    }
    /**
     * Logic of new tool goes here. Default it fallbacks to `this.onMouseUp`.
     */
    onTouchEnd(e) {
        this.onMouseUp(e);
    }
    /**
     * This method is called when tool is changed (destroyed).
     *
     * Place here any logic like removing listeners, clearing intervals or unsubscribing.
     */
    teardown() { }
    /**
     * Update ctx properties to the current size and color.
     */
    updateCtxStyle(ctx) {
        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.size;
    }
    /**
     * Clears entire content in passed context.
     */
    clearCtx(ctx) {
        const { width, height } = ctx.canvas;
        ctx.clearRect(0, 0, width, height);
    }
    /**
     * Returns current position of cursor in canvas context origin.
     */
    getMousePosition(e) {
        const { pageX, pageY } = e instanceof MouseEvent ? e : e.changedTouches[0];
        const { left, top } = this.animCtx.canvas.getBoundingClientRect();
        const { width, height, offsetWidth, offsetHeight } = this.mainCtx.canvas;
        return {
            x: (pageX - left) * (width / offsetWidth),
            y: (pageY - top) * (height / offsetHeight),
        };
    }
}

class Brush extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    setColor(color) {
        super.setColor(color);
        if (color.slice(-2) === 'ff') {
            this.mainCtx.globalCompositeOperation = 'source-over';
        }
        else {
            this.mainCtx.globalCompositeOperation = 'xor';
        }
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.drawing = true;
        this.mouseMoved = false;
        this.updateCtxStyle(this.mainCtx);
        // begin path for mousemove drawing
        this.mainCtx.beginPath();
        this.mainCtx.moveTo(x, y);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x, y } = this.getMousePosition(e);
            this.mainCtx.lineTo(x, y);
            this.mainCtx.closePath();
            this.mainCtx.stroke();
            this.mainCtx.beginPath();
            this.mainCtx.moveTo(x, y);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (!this.mouseMoved) {
                this.drawDot(e);
            }
            this.updateState();
            this.mouseMoved = false;
        }
    }
    drawDot(e) {
        const { x, y } = this.getMousePosition(e);
        this.mainCtx.arc(x, y, 1.25, 0, 2 * Math.PI);
        this.mainCtx.stroke();
    }
}

class Eraser extends Brush {
    constructor(mainCtx, animCtx, updateState) {
        super(mainCtx, animCtx, updateState);
        mainCtx.globalCompositeOperation = 'destination-out';
    }
    setColor(color) {
        super.setColor(color);
        this.mainCtx.globalCompositeOperation = 'destination-out';
    }
    drawDot(e) {
        const { x, y } = this.getMousePosition(e);
        this.mainCtx.arc(x, y, 1, 0, 2 * Math.PI);
        this.mainCtx.stroke();
    }
}

class Line extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.startingPoint = { x, y };
        this.drawing = true;
        this.mouseMoved = false;
        this.updateCtxStyle(this.animCtx);
        this.clearCtx(this.animCtx);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x: startX, y: startY } = this.startingPoint;
            const { x: endX, y: endY } = this.getMousePosition(e);
            this.clearCtx(this.animCtx);
            this.drawLine(this.animCtx, startX, startY, endX, endY);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (this.mouseMoved) {
                const { x: startX, y: startY } = this.startingPoint;
                const { x: endX, y: endY } = this.getMousePosition(e);
                this.clearCtx(this.animCtx);
                this.drawLine(this.mainCtx, startX, startY, endX, endY);
                this.updateState();
            }
            this.mouseMoved = false;
        }
    }
    drawLine(ctx, startX, startY, endX, endY) {
        this.updateCtxStyle(ctx);
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
    }
}

class Rectangle extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.startingPoint = { x, y };
        this.drawing = true;
        this.mouseMoved = false;
        this.updateCtxStyle(this.animCtx);
        this.clearCtx(this.animCtx);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x: startX, y: startY } = this.startingPoint;
            const { x: endX, y: endY } = this.getMousePosition(e);
            this.clearCtx(this.animCtx);
            this.drawRect(this.animCtx, startX, startY, endX, endY);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (this.mouseMoved) {
                const { x: startX, y: startY } = this.startingPoint;
                const { x: endX, y: endY } = this.getMousePosition(e);
                this.clearCtx(this.animCtx);
                this.drawRect(this.mainCtx, startX, startY, endX, endY);
                this.updateState();
            }
            this.mouseMoved = false;
        }
    }
    drawRect(ctx, startX, startY, endX, endY) {
        this.updateCtxStyle(ctx);
        ctx.strokeRect(startX, startY, endX - startX, endY - startY);
    }
}

class Circle extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.startingPoint = { x, y };
        this.drawing = true;
        this.mouseMoved = false;
        this.clearCtx(this.animCtx);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x: startX, y: startY } = this.startingPoint;
            const { x: endX, y: endY } = this.getMousePosition(e);
            this.clearCtx(this.animCtx);
            this.drawCircle(this.animCtx, startX, startY, endX, endY);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (this.mouseMoved) {
                const { x: startX, y: startY } = this.startingPoint;
                const { x: endX, y: endY } = this.getMousePosition(e);
                this.clearCtx(this.animCtx);
                this.drawCircle(this.mainCtx, startX, startY, endX, endY);
                this.updateState();
            }
            this.mouseMoved = false;
        }
    }
    drawCircle(ctx, startX, startY, endX, endY) {
        this.updateCtxStyle(ctx);
        const radius = Math.hypot(endX - startX, endY - startY);
        ctx.beginPath();
        ctx.arc(startX, startY, radius, 0, Math.PI * 2, true);
        ctx.stroke();
    }
}

var ToolName;
(function (ToolName) {
    ToolName["Brush"] = "brush";
    ToolName["Eraser"] = "eraser";
    ToolName["Line"] = "line";
    ToolName["Rectangle"] = "rectangle";
    ToolName["Circle"] = "circle";
    ToolName["RectangularTriangle"] = "rectangular triangle";
    ToolName["IsoscelesTriangle"] = "isosceles triangle";
    ToolName["Highlighter"] = "highlighter";
})(ToolName || (ToolName = {}));

class Highlighter extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.startingPoint = { x, y };
        this.drawing = true;
        this.mouseMoved = false;
        this.updateCtxStyle(this.animCtx);
        this.clearCtx(this.animCtx);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x: startX, y: startY } = this.startingPoint;
            const { x: endX, y: endY } = this.getMousePosition(e);
            this.clearCtx(this.animCtx);
            this.drawRect(this.animCtx, startX, startY, endX, endY);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (this.mouseMoved) {
                const { x: startX, y: startY } = this.startingPoint;
                const { x: endX, y: endY } = this.getMousePosition(e);
                this.clearCtx(this.animCtx);
                this.drawRect(this.mainCtx, startX, startY, endX, endY);
                this.updateState();
            }
            this.mouseMoved = false;
        }
    }
    drawRect(ctx, startX, startY, endX, endY) {
        this.updateCtxStyle(ctx);
        ctx.fillRect(startX, startY, endX - startX, endY - startY);
    }
    updateCtxStyle(ctx) {
        ctx.strokeStyle = this.color;
        ctx.fillStyle = this.color;
        ctx.lineWidth = this.size;
    }
}

class RectangularTriangle extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.startingPoint = { x, y };
        this.drawing = true;
        this.mouseMoved = false;
        this.updateCtxStyle(this.animCtx);
        this.clearCtx(this.animCtx);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x: startX, y: startY } = this.startingPoint;
            const { x: endX, y: endY } = this.getMousePosition(e);
            this.clearCtx(this.animCtx);
            this.drawTriangle(this.animCtx, startX, startY, endX, endY);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (this.mouseMoved) {
                const { x: startX, y: startY } = this.startingPoint;
                const { x: endX, y: endY } = this.getMousePosition(e);
                this.clearCtx(this.animCtx);
                this.drawTriangle(this.mainCtx, startX, startY, endX, endY);
                this.updateState();
            }
            this.mouseMoved = false;
        }
    }
    drawTriangle(ctx, startX, startY, endX, endY) {
        this.updateCtxStyle(ctx);
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(startX, endY);
        ctx.lineTo(endX, startY);
        ctx.closePath();
        ctx.stroke();
    }
}

class IsoscelesTriangle extends Tool {
    constructor() {
        super(...arguments);
        this.drawing = false;
        this.mouseMoved = false;
    }
    onMouseDown(e) {
        if (this.isLocked) {
            return;
        }
        const { x, y } = this.getMousePosition(e);
        this.startingPoint = { x, y };
        this.drawing = true;
        this.mouseMoved = false;
        this.updateCtxStyle(this.animCtx);
        this.clearCtx(this.animCtx);
    }
    onMouseMove(e) {
        if (!this.isLocked && this.drawing) {
            this.mouseMoved = true;
            const { x: startX, y: startY } = this.startingPoint;
            const { x: endX, y: endY } = this.getMousePosition(e);
            this.clearCtx(this.animCtx);
            this.drawTriangle(this.animCtx, startX, startY, endX, endY);
        }
    }
    onMouseUp(e) {
        if (!this.isLocked && this.drawing) {
            this.drawing = false;
            if (this.mouseMoved) {
                const { x: startX, y: startY } = this.startingPoint;
                const { x: endX, y: endY } = this.getMousePosition(e);
                this.clearCtx(this.animCtx);
                this.drawTriangle(this.mainCtx, startX, startY, endX, endY);
                this.updateState();
            }
            this.mouseMoved = false;
        }
    }
    drawTriangle(ctx, startX, startY, endX, endY) {
        this.updateCtxStyle(ctx);
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo((endX + startX) / 2, endY);
        ctx.lineTo(endX, startY);
        ctx.closePath();
        ctx.stroke();
    }
}

const ToolsMap = {
    [ToolName.Brush]: Brush,
    [ToolName.Eraser]: Eraser,
    [ToolName.Line]: Line,
    [ToolName.Rectangle]: Rectangle,
    [ToolName.Circle]: Circle,
    [ToolName.RectangularTriangle]: RectangularTriangle,
    [ToolName.IsoscelesTriangle]: IsoscelesTriangle,
    [ToolName.Highlighter]: Highlighter,
};

const ERASER_PREVIEW_COLOR = 'rgba(128, 128, 128, 0.3)';
const BRUSH_PREVIEW_COLOR = 'rgba(255, 255, 255, 0)';
class TextbookDrawerComponent {
    constructor(service, ngZone) {
        this.service = service;
        this.ngZone = ngZone;
        /**
         * Width of canvas context. Default: 900
         */
        this.width = CANVAS_DEFAULT_WIDTH_PX;
        /**
         * Height of canvas context. Default: 900
         */
        this.height = CANVAS_DEFAULT_HEIGHT_PX;
        /**
         * Maximum amount of states stored for undo commands. Default: 10
         */
        this.maxUndoLength = 10;
        this.headerHeight = '0px';
        this.footerHeight = '0px';
        this.vh = null;
        this.fullSize = false;
        this.uniqueClass = `gwo-textbook-drawer-${v4().toString()}`;
        this.mousePosition$ = new BehaviorSubject(undefined);
        this.previewCursorVisible$ = new BehaviorSubject(false);
        this.previewCursorSize$ = new BehaviorSubject(0);
        this.previewCursorColor$ = new BehaviorSubject(ERASER_PREVIEW_COLOR);
        this.currentCursor = 'auto';
        this.dataUrlToLoad$ubject = new Subject();
        this.subscription = new Subscription();
        this.undoStates = [];
        this.redoStates = [];
    }
    static getMousePosition(e) {
        const { clientX: x, clientY: y } = e instanceof MouseEvent ? e : e.touches[0];
        return { x, y };
    }
    ngOnInit() {
        this.ngZone.runOutsideAngular(() => {
            [
                fromEvent(window, 'mousedown').pipe(tap((e) => this.onMouseDown(e))),
                fromEvent(window, 'mousemove').pipe(tap((e) => this.onMouseMove(e))),
                fromEvent(window, 'mouseup').pipe(tap((e) => this.onMouseUp(e))),
                fromEvent(window, 'touchstart', { passive: false }).pipe(tap((e) => this.onTouchStart(e))),
                fromEvent(window, 'touchmove', { passive: false }).pipe(tap((e) => this.onTouchMove(e))),
                fromEvent(window, 'touchend', { passive: false }).pipe(tap((e) => this.onTouchEnd(e))),
            ].forEach((event$) => this.subscription.add(event$.subscribe()));
        });
    }
    ngAfterViewInit() {
        const mainCanvas = this.mainCanvasRef.nativeElement;
        this.service._setMainCanvas(mainCanvas);
        const mainCtx = mainCanvas.getContext('2d');
        const animCtx = this.animCanvasRef.nativeElement.getContext('2d');
        if (!mainCtx || !animCtx) {
            throw new Error('Textbook drawer failed initialization');
        }
        this.subscription.add(this.service.toolName$.subscribe((name) => {
            var _a;
            (_a = this.tool) === null || _a === void 0 ? void 0 : _a.teardown();
            this.tool =
                name && new ToolsMap[name](mainCtx, animCtx, this.updateCurrentState.bind(this));
            this.setCursor(name);
        }));
        this.subscription.add(combineLatest([this.service.toolName$, this.service.color$])
            .pipe(filter(([, color]) => !!color))
            .subscribe(([toolName, color]) => {
            var _a, _b;
            this.setPreviewCursorColor(toolName, color);
            (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.setColor) === null || _b === void 0 ? void 0 : _b.call(_a, color);
        }));
        this.subscription.add(combineLatest([this.service.toolName$, this.service.size$])
            .pipe(filter(([, size]) => !!size))
            .subscribe(([, size]) => {
            var _a, _b;
            this.setPreviewCursorSize(size);
            (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.setSize) === null || _b === void 0 ? void 0 : _b.call(_a, size);
        }));
        this.subscription.add(this.service.command$.subscribe((command) => this.handleCommand(command)));
        this.subscription.add(this.dataUrlToLoad$ubject
            .pipe(tap(() => {
            var _a;
            this.service._setIsLoading(true);
            (_a = this.tool) === null || _a === void 0 ? void 0 : _a.setLocked(true);
        }), switchMap(({ dataUrl, saveState }) => {
            if (dataUrl) {
                return this.loadImage$(dataUrl).pipe(map(() => saveState));
            }
            this.clear();
            return of(saveState);
        }), tap((saveState) => {
            if (saveState) {
                this.updateCurrentState();
            }
            else {
                this.updateOutputs();
            }
        }), observeOn(asyncScheduler), tap(() => {
            var _a;
            this.service._setIsLoading(false);
            (_a = this.tool) === null || _a === void 0 ? void 0 : _a.setLocked(false);
        }))
            .subscribe());
        this.subscription.add(this.service.isLocked$.subscribe((isLocked) => { var _a; return (_a = this.tool) === null || _a === void 0 ? void 0 : _a.setLocked(isLocked); }));
        this.subscription.add(combineLatest([this.mousePosition$, this.previewCursorVisible$, this.previewCursorSize$])
            .pipe(filter(() => Boolean(this.eraserCursor.nativeElement)), tap(([mousePosition, cursorVisible]) => {
            const isVisible = Boolean(mousePosition) && Boolean(cursorVisible) ? 'visible' : 'hidden';
            const previewCursorStyle = this.eraserCursor.nativeElement.style;
            if (previewCursorStyle.visibility !== isVisible) {
                previewCursorStyle.visibility = isVisible;
            }
        }), filter(([mousePosition, cursorVisible]) => Boolean(mousePosition) && Boolean(cursorVisible)))
            .subscribe(([mousePosition, _, size]) => {
            const eraserShadowPosition = this.calcEraserShadowPosition(mousePosition, size);
            this.eraserCursor.nativeElement.style.transform =
                'translate(' + eraserShadowPosition.x + 'px,' + eraserShadowPosition.y + 'px)';
        }));
        this.updateCurrentState();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.dataUrlToLoad$ubject.complete();
        this.setCursor(undefined);
        this.updateDomCursor(this.currentCursor);
    }
    onMouseDown(e) {
        var _a, _b;
        if (this.isOffline) {
            return;
        }
        if (this.touchEndTimeStamp !== e.timeStamp) {
            (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.onMouseDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
    }
    onMouseMove(e) {
        var _a, _b;
        if (this.isOffline) {
            return;
        }
        if (this.touchEndTimeStamp !== e.timeStamp) {
            this.updateMousePosition(e);
            const cursor = this.isOverCanvas(e) ? this.currentCursor : 'auto';
            this.updateDomCursor(cursor);
            (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.onMouseMove) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
    }
    onMouseUp(e) {
        var _a, _b;
        if (this.touchEndTimeStamp !== e.timeStamp) {
            (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.onMouseUp) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        this.eraserCursor.nativeElement.style.visibility = 'hidden';
    }
    onTouchStart(e) {
        var _a, _b;
        if (this.isOffline) {
            return;
        }
        (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.onTouchStart) === null || _b === void 0 ? void 0 : _b.call(_a, e);
    }
    onTouchMove(e) {
        var _a, _b;
        if (!!this.service.getToolName()) {
            e.preventDefault();
        }
        if (this.isOffline) {
            return;
        }
        if (this.touchEndTimeStamp !== e.timeStamp) {
            this.updateMousePosition(e);
            const cursor = this.isOverCanvas(e) ? this.currentCursor : 'auto';
            this.updateDomCursor(cursor);
            (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.onTouchMove) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
    }
    onTouchEnd(e) {
        var _a, _b;
        (_b = (_a = this.tool) === null || _a === void 0 ? void 0 : _a.onTouchEnd) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        this.eraserCursor.nativeElement.style.visibility = 'hidden';
        this.touchEndTimeStamp = e.timeStamp;
    }
    handleCommand(command) {
        if (command instanceof ClearCommand) {
            this.loadImage({ dataUrl: undefined, saveState: true });
        }
        else if (command instanceof LoadCommand && command.state !== this.currentState) {
            this.loadImage({ dataUrl: command.state, saveState: true });
        }
        else if (command instanceof UndoCommand) {
            this.undo();
        }
        else if (command instanceof RedoCommand) {
            this.redo();
        }
    }
    updateCurrentState() {
        if (this.undoStates.length >= this.maxUndoLength) {
            this.undoStates.shift();
        }
        if (this.currentState) {
            this.undoStates.push(this.currentState);
        }
        this.currentState = this.getImageUrl();
        this.redoStates = [];
        this.updateOutputs();
    }
    getImageUrl() {
        var _a;
        return ((_a = this.mainCanvasRef) === null || _a === void 0 ? void 0 : _a.nativeElement.toDataURL()) || '';
    }
    loadImage({ dataUrl, saveState }) {
        this.dataUrlToLoad$ubject.next({ dataUrl, saveState });
    }
    clear() {
        var _a;
        const canvas = (_a = this.mainCanvasRef) === null || _a === void 0 ? void 0 : _a.nativeElement;
        const ctx = canvas.getContext('2d');
        if (canvas && ctx) {
            const prevCompositeOperation = ctx.globalCompositeOperation;
            ctx.globalCompositeOperation = 'source-over';
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = prevCompositeOperation;
        }
    }
    undo() {
        if (this.undoStates.length) {
            const lastUndoState = this.undoStates.pop();
            if (!lastUndoState) {
                return;
            }
            this.redoStates.push(this.currentState);
            this.currentState = lastUndoState;
            this.loadImage({ dataUrl: this.currentState, saveState: false });
        }
    }
    redo() {
        if (this.redoStates.length) {
            const lastRedoState = this.redoStates.pop();
            if (!lastRedoState) {
                return;
            }
            this.undoStates.push(this.currentState);
            this.currentState = lastRedoState;
            this.loadImage({ dataUrl: this.currentState, saveState: false });
        }
    }
    updateOutputs() {
        this.service._setCurrentState(this.currentState);
        this.service._setUndoLength(this.undoStates.length);
        this.service._setRedoLength(this.redoStates.length);
    }
    loadImage$(dataUrl) {
        return new Observable((observer) => {
            var _a;
            const ctx = (_a = this.mainCanvasRef) === null || _a === void 0 ? void 0 : _a.nativeElement.getContext('2d');
            if (!ctx) {
                observer.next(dataUrl);
                observer.complete();
                return;
            }
            const startTime = new Date().getTime();
            const img = new Image();
            img.src = dataUrl;
            img.onload = () => {
                const ctxWidth = ctx.canvas.width;
                const ctxHeight = ctx.canvas.height;
                const widthRatio = ctxWidth / img.naturalWidth;
                const heightRatio = ctxHeight / img.naturalHeight;
                const ratio = Math.min(widthRatio, heightRatio);
                const imgHeight = img.naturalHeight * ratio;
                const imgWidth = img.naturalWidth * ratio;
                const startX = (ctxWidth - imgWidth) / 2;
                const startY = (ctxHeight - imgHeight) / 2;
                this.clear();
                const prevCompositeOperation = ctx.globalCompositeOperation;
                ctx.globalCompositeOperation = 'source-over';
                ctx.drawImage(img, startX, startY, imgWidth, imgHeight);
                ctx.globalCompositeOperation = prevCompositeOperation;
                const endTime = new Date().getTime();
                console.log(`Image loaded in: ${endTime - startTime} ms`);
                observer.next(dataUrl);
                observer.complete();
            };
            return () => {
                img.src = '';
            };
        });
    }
    updateMousePosition(e) {
        const position = this.isOverCanvas(e) ? TextbookDrawerComponent.getMousePosition(e) : undefined;
        this.mousePosition$.next(position);
    }
    setPreviewCursorColor(toolName, color) {
        const previewColor = toolName === ToolName.Eraser
            ? ERASER_PREVIEW_COLOR
            : toolName === ToolName.Brush
                ? BRUSH_PREVIEW_COLOR
                : color;
        this.previewCursorColor$.next(previewColor);
    }
    setPreviewCursorSize(size) {
        const initialWidth = this.mainCanvasRef.nativeElement.width;
        const currentWidth = this.mainCanvasRef.nativeElement.clientWidth;
        this.previewCursorSize$.next((size * currentWidth) / initialWidth);
    }
    setCursor(toolName) {
        let cursor = 'auto';
        this.previewCursorVisible$.next(false);
        if (toolName === ToolName.Brush) {
            const img = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAaCAYAAACpSkzOAAABVklEQVRIS7XWy3GDMBAA0F2Le9xBSojdgdOBXUGSu8QoFYA70IDuIR24g7iDUALpAN/RbIYMZLD5CmTOoDf7YVcIjh8hxCsABIgYRlH0WR+PLp0K+WicmTHGtkqp3BnUgdTeKY7jgxNoAKmx98WQlHJjjPkeKUG2CKqQLwBYj0BvsyEbJI7jZBZki5TRWkNzEGtoLmIFTUQuRLTXWp9vm2NS6qYijLGdUirt6sBRyAUymjpXyCDkEumFXCO9kBAiB4CHgbFyGSr8aDNIKddFUWw8z8uNMQkAPHV8ZI20IvJ9PySigIhCrfWRc54g4ksDm4W0oGbKiCjVWm+FEOV/UUY2G7mCupYXIh5Xq9XJGHO2rUnvZBBCZADwePsCET17npeWe3/J/eJvMvSs4h8ACMtdsgS4ugVV3aaqwjsF/iHO+Q4RA8bYwRizdxVBq0ZVS2f3AmrwF78u25+hop4YAAAAAElFTkSuQmCC';
            cursor = `url('${img}') 0 24, crosshair`;
            this.previewCursorVisible$.next(true);
        }
        else if (toolName === ToolName.Eraser) {
            const img = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAACZElEQVRYR7VX0ZHaMBTch1zAdRBKIBWEVJBLB9w/YpQKcunAsfx/XAUhFRxUcFwFIR0k/9Yo84SdsY0sCxD+YQbL2vW+t6tnQqJLSrkA8BXARgjxnOf5PmZrilk0tqYGf+qtO1hr8yzLfuZ5fhja42oCA+AdPGstq7H2kbmKQAy45803RLSZTCaszJ+LCSilZsaYFwB3YyUaui+EeH8RgRTgAL5orfOzCaQAt9Y+l2W5kFI+nUUgNTiARTSBFOAA3oQQc2PMPQBn2ygCtwKPIpAI/C+/OQP2nRNU4NbgQQUSgcNa+znLsq0x5hXAtJcJv70KKKXujDG/rgmZGuhBCLGpquqFiGY9cFeWEwIMPvDAWYFHRN+LolBSyh8AuOv758PHsiy3HQKpwNtBw173MH/QWq87PZAKHMBOaz1frVaP1lqeDzpXo0zzp1MgIfhJ0LTRG2Xa/1FCcG6qaVVVMyLiU7J/vWmt+414TEKl1LSORw6LT2d123HxYNDUezll+Pw/KYk7kY4Dwo4X1IowkXsi4t93Y4T4XAdwGLCuIzc0I5KU0jYA9ei0qUcnN1TWgeQIAfjg6+iA18HkQgNqh0Bvc5aLx6dtMz551Ml5qFgul6+eoOHt/tttSMUQgX5w7JkMk9Ja75qbXEKf14noW1EUj2PliybgUYdHqnW7hK1SuolnDJzvX0oAzRt6CLggigG/BYFBu13dAyf+rWvcUoDtNgt9BflIuCTkELLWzmN976TrEgh6PVQO33HMHxwh37v9egRG7TZYghC7UCq2CCjOgtimOynlOQ+2zwwi2sf4fGz/f553lZuFZ4l4AAAAAElFTkSuQmCC';
            cursor = `url('${img}') 0 29, crosshair`;
            this.previewCursorVisible$.next(true);
        }
        else if (toolName) {
            cursor = 'crosshair';
        }
        this.currentCursor = cursor;
    }
    isOverCanvas(e) {
        const { left, top, width, height } = this.animCanvasRef.nativeElement.getBoundingClientRect();
        const { x, y } = TextbookDrawerComponent.getMousePosition(e);
        return x >= left && x <= left + width && y >= top && y <= top + height;
    }
    updateDomCursor(cursor) {
        const bodyStyle = document.body.style;
        if (bodyStyle.cursor !== cursor) {
            bodyStyle.cursor = cursor;
        }
    }
    calcEraserShadowPosition(mousePosition, size) {
        var _a;
        const drawerClientRect = (_a = document.querySelector(`.${this.uniqueClass}`)) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const offsetY = (drawerClientRect === null || drawerClientRect === void 0 ? void 0 : drawerClientRect.top) + 1;
        const offsetX = (drawerClientRect === null || drawerClientRect === void 0 ? void 0 : drawerClientRect.left) + 1;
        return {
            x: Number(mousePosition === null || mousePosition === void 0 ? void 0 : mousePosition.x) - offsetX - size / 2,
            y: Number(mousePosition === null || mousePosition === void 0 ? void 0 : mousePosition.y) - offsetY - size / 2
        };
    }
}
TextbookDrawerComponent.ɵfac = function TextbookDrawerComponent_Factory(t) { return new (t || TextbookDrawerComponent)(ɵngcc0.ɵɵdirectiveInject(TextbookDrawerService), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.NgZone)); };
TextbookDrawerComponent.ɵcmp = /*@__PURE__*/ ɵngcc0.ɵɵdefineComponent({ type: TextbookDrawerComponent, selectors: [["gwo-textbook-drawer"]], viewQuery: function TextbookDrawerComponent_Query(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵviewQuery(_c0, 5);
        ɵngcc0.ɵɵviewQuery(_c1, 5);
        ɵngcc0.ɵɵviewQuery(_c2, 5);
    } if (rf & 2) {
        let _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.mainCanvasRef = _t.first);
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.animCanvasRef = _t.first);
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.eraserCursor = _t.first);
    } }, hostVars: 8, hostBindings: function TextbookDrawerComponent_HostBindings(rf, ctx) { if (rf & 2) {
        ɵngcc0.ɵɵclassMap(ctx.uniqueClass);
        ɵngcc0.ɵɵstyleProp("--header-height", ctx.headerHeight)("--footer-height", ctx.footerHeight)("--vh", ctx.vh);
    } }, inputs: { width: "width", height: "height", maxUndoLength: "maxUndoLength", headerHeight: "headerHeight", footerHeight: "footerHeight", vh: "vh", fullSize: "fullSize", isOffline: "isOffline" }, decls: 8, vars: 18, consts: [[1, "mainCanvas", 3, "width", "height", "ngClass"], ["main", ""], [1, "animCanvas", 3, "width", "height", "ngClass"], ["anim", ""], [1, "eraser-cursor"], ["eraserCursor", ""]], template: function TextbookDrawerComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵelement(0, "canvas", 0, 1);
        ɵngcc0.ɵɵelement(2, "canvas", 2, 3);
        ɵngcc0.ɵɵelementStart(4, "div", 4, 5);
        ɵngcc0.ɵɵpipe(6, "async");
        ɵngcc0.ɵɵpipe(7, "async");
        ɵngcc0.ɵɵelementEnd();
    } if (rf & 2) {
        ɵngcc0.ɵɵproperty("width", ctx.width)("height", ctx.height)("ngClass", ɵngcc0.ɵɵpureFunction1(14, _c3, ctx.fullSize));
        ɵngcc0.ɵɵadvance(2);
        ɵngcc0.ɵɵproperty("width", ctx.width)("height", ctx.height)("ngClass", ɵngcc0.ɵɵpureFunction1(16, _c3, ctx.fullSize));
        ɵngcc0.ɵɵadvance(2);
        ɵngcc0.ɵɵstyleProp("--size", ɵngcc0.ɵɵpipeBind1(6, 10, ctx.previewCursorSize$) + "px")("--color", ɵngcc0.ɵɵpipeBind1(7, 12, ctx.previewCursorColor$));
    } }, directives: [ɵngcc1.NgClass], pipes: [ɵngcc1.AsyncPipe], styles: ["[_nghost-%COMP%]{display:block;position:relative;width:100%;max-width:100%;height:100%;pointer-events:none;padding:1px 0}.mainCanvas[_ngcontent-%COMP%]{border:1px dashed #707070;border-radius:10px}.animCanvas[_ngcontent-%COMP%], .mainCanvas[_ngcontent-%COMP%]{display:block;position:absolute;top:50%;left:50%;width:calc(var(--vh, 1vh) * 85 / .5);max-width:85vw;height:42.5vw;max-height:calc((var(--vh, 1vh) * 85) - env(safe-area-inset-bottom));transform:translate(-50%,-50%)}.animCanvas.is-full-size[_ngcontent-%COMP%], .mainCanvas.is-full-size[_ngcontent-%COMP%]{width:100%;height:100%;max-width:unset;max-height:unset;border:none;border-radius:unset}@media screen and (max-height: 550px){.animCanvas[_ngcontent-%COMP%], .mainCanvas[_ngcontent-%COMP%]{width:calc(var(--vh, 1vh) * 75 / .5);max-width:75vw;height:37.5vw;max-height:min(100vh - var(--footer-height) - var(--header-height),75vh - env(safe-area-inset-bottom))}.animCanvas.is-sandbox-drawing[_ngcontent-%COMP%], .mainCanvas.is-sandbox-drawing[_ngcontent-%COMP%]{width:100%;height:100%;max-width:unset;max-height:unset}}.eraser-cursor[_ngcontent-%COMP%]{position:fixed;width:var(--size);height:var(--size);transform:translate(-20%,-20%);border-radius:50%;background-color:var(--color);pointer-events:none}"], changeDetection: 0 });
TextbookDrawerComponent.ctorParameters = () => [
    { type: TextbookDrawerService },
    { type: NgZone }
];
TextbookDrawerComponent.propDecorators = {
    width: [{ type: Input }],
    height: [{ type: Input }],
    maxUndoLength: [{ type: Input }],
    isOffline: [{ type: Input }],
    headerHeight: [{ type: Input }, { type: HostBinding, args: ["style.--header-height",] }],
    footerHeight: [{ type: Input }, { type: HostBinding, args: ["style.--footer-height",] }],
    vh: [{ type: Input }, { type: HostBinding, args: ["style.--vh",] }],
    fullSize: [{ type: Input }],
    mainCanvasRef: [{ type: ViewChild, args: ['main',] }],
    animCanvasRef: [{ type: ViewChild, args: ['anim',] }],
    eraserCursor: [{ type: ViewChild, args: ['eraserCursor',] }],
    uniqueClass: [{ type: HostBinding, args: ['class',] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TextbookDrawerComponent, [{
        type: Component,
        args: [{
                selector: 'gwo-textbook-drawer',
                template: "<canvas\n  #main\n  [width]=\"width\"\n  [height]=\"height\"\n  class=\"mainCanvas\"\n  [ngClass]=\"{ 'is-full-size': fullSize }\"\n></canvas>\n\n<canvas\n  #anim\n  [width]=\"width\"\n  [height]=\"height\"\n  class=\"animCanvas\"\n  [ngClass]=\"{ 'is-full-size': fullSize }\"\n></canvas>\n\n<div\n  class=\"eraser-cursor\"\n  #eraserCursor\n  [style.--size]=\"(previewCursorSize$ | async) + 'px'\"\n  [style.--color]=\"previewCursorColor$ | async\"\n></div>\n",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [":host{display:block;position:relative;width:100%;max-width:100%;height:100%;pointer-events:none;padding:1px 0}.mainCanvas{border:1px dashed #707070;border-radius:10px}.animCanvas,.mainCanvas{display:block;position:absolute;top:50%;left:50%;width:calc(var(--vh, 1vh) * 85 / .5);max-width:85vw;height:42.5vw;max-height:calc((var(--vh, 1vh) * 85) - env(safe-area-inset-bottom));transform:translate(-50%,-50%)}.animCanvas.is-full-size,.mainCanvas.is-full-size{width:100%;height:100%;max-width:unset;max-height:unset;border:none;border-radius:unset}@media screen and (max-height: 550px){.animCanvas,.mainCanvas{width:calc(var(--vh, 1vh) * 75 / .5);max-width:75vw;height:37.5vw;max-height:min(100vh - var(--footer-height) - var(--header-height),75vh - env(safe-area-inset-bottom))}.animCanvas.is-sandbox-drawing,.mainCanvas.is-sandbox-drawing{width:100%;height:100%;max-width:unset;max-height:unset}}.eraser-cursor{position:fixed;width:var(--size);height:var(--size);transform:translate(-20%,-20%);border-radius:50%;background-color:var(--color);pointer-events:none}\n"]
            }]
    }], function () { return [{ type: TextbookDrawerService }, { type: ɵngcc0.NgZone }]; }, { width: [{
            type: Input
        }], height: [{
            type: Input
        }], maxUndoLength: [{
            type: Input
        }], headerHeight: [{
            type: Input
        }, {
            type: HostBinding,
            args: ["style.--header-height"]
        }], footerHeight: [{
            type: Input
        }, {
            type: HostBinding,
            args: ["style.--footer-height"]
        }], vh: [{
            type: Input
        }, {
            type: HostBinding,
            args: ["style.--vh"]
        }], fullSize: [{
            type: Input
        }], uniqueClass: [{
            type: HostBinding,
            args: ['class']
        }], isOffline: [{
            type: Input
        }], mainCanvasRef: [{
            type: ViewChild,
            args: ['main']
        }], animCanvasRef: [{
            type: ViewChild,
            args: ['anim']
        }], eraserCursor: [{
            type: ViewChild,
            args: ['eraserCursor']
        }] }); })();

class TextbookDrawerModule {
}
TextbookDrawerModule.ɵfac = function TextbookDrawerModule_Factory(t) { return new (t || TextbookDrawerModule)(); };
TextbookDrawerModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: TextbookDrawerModule });
TextbookDrawerModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TextbookDrawerModule, [{
        type: NgModule,
        args: [{
                declarations: [TextbookDrawerComponent],
                exports: [TextbookDrawerComponent],
                imports: [CommonModule]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(TextbookDrawerModule, { declarations: function () { return [TextbookDrawerComponent]; }, imports: function () { return [CommonModule]; }, exports: function () { return [TextbookDrawerComponent]; } }); })();

/*
 * Public API Surface of textbook-drawer
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CANVAS_DEFAULT_HEIGHT_PX, CANVAS_DEFAULT_WIDTH_PX, ClearCommand, CommandName, LoadCommand, RedoCommand, TextbookDrawerComponent, TextbookDrawerModule, TextbookDrawerService, ToolName, UndoCommand };

//# sourceMappingURL=gwo-textbook-drawer.js.map